import { NoData, SkeletonLoader } from '@/components';
import { useActivities } from '@/hooks/useActivities';
import { useGetInitialValue } from '@/hooks/useGetInitialValue';
import { black, titleStyle } from '@/utils/common';
import { formatWeekNumber } from '@/utils/date';
import { getTriathlonStats } from '@/utils/getTriathlonStats';
import { roundNumber } from '@/utils/maths';
import { getTSS } from '@/utils/metrics';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import styles from '../style.module.css';

/**
 * charge de la semaine: somme de toutes les charges
 * monotonie: charge moyenne de la semaine / écart type de la charge de la semaine
 * contrainte: charge semaine * monotonie
 * forme: charge semaine - contrainte
 */

const Fitness = () => {
  const { activities, isLoading } = useActivities();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const { initialValue } = useGetInitialValue();

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  const { allActivitiesStats } = getTriathlonStats(
    activities,
    'tss',
    false,
    initialValue
  );

  const total = activities.reduce(
    (acc, activity) => acc + (getTSS(activity) || 0),
    0
  );
  const nbWeeks = Object.values(allActivitiesStats).filter((e) => e).length;
  const mean = roundNumber(total / nbWeeks);

  const categories = Object.keys(allActivitiesStats).map((week) =>
    formatWeekNumber(Number(week))
  );

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Charge',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories,
      title: {
        text: 'week',
      },
    },
    series: [
      {
        name: 'charge',
        color: black,
        type: 'line',
        data: Object.values(allActivitiesStats),
      },
      {
        name: 'mean',
        color: black,
        data: [
          [0, mean],
          [nbWeeks, mean],
        ],
        type: 'line',
      },
    ],
  };

  return (
    <div id="fitness">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default Fitness;
